import React, { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Zap, Shield, Share2, Layers } from 'lucide-react';
import SplitText from '../common/SplitText';

const TiltCard = ({ children, className }) => {
    const ref = useRef(null);
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const mouseX = useSpring(x, { stiffness: 500, damping: 100 });
    const mouseY = useSpring(y, { stiffness: 500, damping: 100 });

    const rotateX = useTransform(mouseY, [-0.5, 0.5], ["15deg", "-15deg"]);
    const rotateY = useTransform(mouseX, [-0.5, 0.5], ["-15deg", "15deg"]);

    const handleMouseMove = (e) => {
        const rect = ref.current.getBoundingClientRect();

        const width = rect.width;
        const height = rect.height;

        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const xPct = mouseX / width - 0.5;
        const yPct = mouseY / height - 0.5;

        x.set(xPct);
        y.set(yPct);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

const FeatureBento = () => {
    return (
        <section className="py-32 bg-cyber-black perspective-1000">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        <SplitText>Built for Velocity.</SplitText>
                    </h2>
                    <p className="text-lg text-slate-600">Everything you need to build, test, and deploy pipelines without leaving your browser.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 auto-rows-auto md:auto-rows-[300px]">
                    <TiltCard className="md:col-span-2 bg-cyber-gray rounded-lg p-5 sm:p-8 shadow-[0_0_15px_rgba(0,243,255,0.1)] border border-gray-800 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-80 h-80 bg-neon-blue/10 rounded-full -mr-20 -mt-20 group-hover:scale-125 transition-transform duration-700 ease-out" />
                        <div className="relative z-10" style={{ transform: "translateZ(50px)" }}>
                            <div className="w-12 h-12 bg-neon-blue rounded-lg flex items-center justify-center text-black mb-6 shadow-lg shadow-neon-blue/30">
                                <Zap className="w-6 h-6" />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-4">Instant Execution Engine</h3>
                            <p className="text-slate-600 text-lg max-w-sm">
                                Our optimized runtime executes your DAGs in milliseconds.
                                No cold starts, no overhead. Just pure performance.
                            </p>
                        </div>
                    </TiltCard>

                    <TiltCard className="md:row-span-2 bg-gray-900 rounded-lg p-5 sm:p-8 shadow-2xl border border-gray-800 relative overflow-hidden group text-white">
                        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black" />
                        <div className="relative z-10 h-full flex flex-col justify-between" style={{ transform: "translateZ(50px)" }}>
                            <div>
                                <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center text-white mb-6 shadow-lg shadow-indigo-500/30">
                                    <Shield className="w-6 h-6" />
                                </div>
                                <h3 className="text-2xl font-bold mb-4">Enterprise Security</h3>
                                <p className="text-slate-400">SOC2 compliant. End-to-end encryption. RBAC baked in.</p>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 backdrop-blur-sm border border-slate-700 mt-8">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                    <span className="text-sm font-mono text-green-400">System Secure</span>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-1 w-full bg-slate-700 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{ width: "98%" }}
                                            transition={{ duration: 1, delay: 0.5 }}
                                            className="h-full bg-green-500"
                                        />
                                    </div>
                                    <div className="flex justify-between text-xs text-slate-500 font-mono">
                                        <span>ENCRYPTION</span>
                                        <span>AES-256</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TiltCard>

                    <TiltCard className="bg-cyber-gray rounded-lg p-5 sm:p-8 shadow-[0_0_15px_rgba(188,19,254,0.1)] border border-gray-800 group">
                        <div className="relative z-10" style={{ transform: "translateZ(30px)" }}>
                            <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center text-white mb-6 shadow-lg shadow-teal-500/30">
                                <Share2 className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Real-time Sync</h3>
                            <p className="text-gray-400">Edit pipelines with your team in real-time. No conflicts.</p>
                        </div>
                    </TiltCard>

                    <TiltCard className="bg-cyber-gray rounded-lg p-5 sm:p-8 shadow-[0_0_15px_rgba(255,165,0,0.1)] border border-gray-800 group">
                        <div className="relative z-10" style={{ transform: "translateZ(30px)" }}>
                            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center text-white mb-6 shadow-lg shadow-orange-500/30">
                                <Layers className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-2">Version Control</h3>
                            <p className="text-gray-400">Rollback to any previous state with a single click.</p>
                        </div>
                    </TiltCard>
                </div>
            </div>
        </section>
    );
};

export default FeatureBento;
